import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import contractService from "../../services/contractService";
import FilterPanel from "./FilterPanel";
import Pagination from "../common/Pagination";
import LoadingSpinner from "../common/LoadingSpinner";
import ContractStatisticsModal from "./ContractStatisticsModal";
import { formatDate, formatAmount } from "../../utils/formatters";
import exportService from "../../services/exportService";
import { useAuth } from "../../context/AuthContext";
import photoService from "../../services/photoService";


// Hook personnalisé pour comparer avec les valeurs précédentes
const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const ContractList = () => {
  // Hooks de navigation
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser: user } = useAuth();
  const canManagePhotos = user && (user.role === 'admin' || user.role === 'admin_assistant');

  // États principaux
  const [forceUpdate, setForceUpdate] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tableContainerRef = useRef(null);

  // États de l'interface
  const [tableZoom, setTableZoom] = useState(75);
  const [colorColumnExpanded, setColorColumnExpanded] = useState(false);
  const [stateColumnExpanded, setStateColumnExpanded] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [statisticsModalOpen, setStatisticsModalOpen] = useState(false);

  // 🆕 NOUVEAU : État pour le tri côté serveur
  const [sorting, setSorting] = useState({
    sortBy: "creationDate", // Par défaut tri par date de création
    sortOrder: "desc", // Par défaut décroissant
  });

  // États pour la mémorisation de position (INCHANGÉS - FONCTIONNENT BIEN)
  const [scrollToContractId, setScrollToContractId] = useState(null);
  const [shouldScrollToContract, setShouldScrollToContract] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restorationComplete, setRestorationComplete] = useState(false);

  // 🔧 CORRECTION: Protection optimisée contre les clics multiples + gestion double-clic
  const lastClickTimeRef = useRef(0);
  const lastDoubleClickTimeRef = useRef(0);
  const clickThrottleMs = 50; // Délai réduit pour clic simple
  const doubleClickThrottleMs = 500; // Délai entre double-clics

  // 🔧 NOUVEAU: Variables pour gérer l'interaction clic/double-clic
  const clickTimeoutRef = useRef(null);
  const isDoubleClickRef = useRef(false);

  // 🆕 NOUVEAU : États pour les exports (à ajouter avec les autres états)
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);

  // 🆕 NOUVEAU : États pour le drag & drop de photos
  const [dragOverRowId, setDragOverRowId] = useState(null);
  const [uploadingRowId, setUploadingRowId] = useState(null);

  // États des totaux
  const [totals, setTotals] = useState({
    jewelWeight: 0,
    cdvaWeight: 0,
    cddWeight: 0,
    disbursementAmount: 0,
    expectedAmount: 0,
    receivedAmount: 0,
    remainingAmount: 0,
    discount: 0,
  });

  const [generalTotals, setGeneralTotals] = useState({
    totalCount: 0,
    jewelWeight: 0,
    cdvaWeight: 0,
    cddWeight: 0,
    disbursementAmount: 0,
    expectedAmount: 0,
    receivedAmount: 0,
    remainingAmount: 0,
    discount: 0,
  });

  const [loadingGeneralTotals, setLoadingGeneralTotals] = useState(false);

  // 🆕 MODIFICATION : Fonction pour récupérer les valeurs par défaut des filtres avec statusFilter
  const getCurrentFilters = () => {
    const currentDate = new Date();
    return {
      agency: "",
      year: currentDate.getFullYear().toString(),
      month: currentDate.getMonth() + 1,
      search: "",
      statusFilter: "", // 🆕 NOUVEAU : Filtre par statut
    };
  };

  // États des filtres et pagination
  const [filters, setFilters] = useState(getCurrentFilters());
  const prevFilters = usePrevious(filters);
  const prevSorting = usePrevious(sorting);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  // 🆕 NOUVEAU : État pour activer/désactiver la pagination
  const [paginationEnabled, setPaginationEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false); // 🆕 Plein écran

  const prevPage = usePrevious(pagination.page);
  const prevLimit = usePrevious(pagination.limit);
  const prevPaginationEnabled = usePrevious(paginationEnabled); // 🆕 NOUVEAU
  // =====================================
  // 🆕 NOUVEAU : FONCTIONS DE TRI CÔTÉ SERVEUR
  // =====================================

  // Fonction pour gérer le clic sur l'en-tête de colonne Contrat
  const handleSort = useCallback(() => {
    setSorting((prevSorting) => {
      const newSorting = {
        sortBy: "dossier",
        sortOrder:
          prevSorting.sortBy === "dossier" && prevSorting.sortOrder === "desc"
            ? "asc"
            : "desc",
      };
      console.log("🔄 Nouveau tri:", newSorting);
      return newSorting;
    });

    // Remettre à la page 1 lors d'un changement de tri
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = useCallback(() => {
    if (sorting.sortBy !== "dossier") {
      return <span className="text-gray-400 ml-1">⇅</span>;
    }

    return sorting.sortOrder === "asc" ? (
      <span className="text-blue-600 ml-1">↑</span>
    ) : (
      <span className="text-blue-600 ml-1">↓</span>
    );
  }, [sorting]);

  // Fonction pour obtenir le libellé du tri actuel
  const getSortLabel = useCallback(() => {
    if (sorting.sortBy === "dossier") {
      return `Trié par N° Dossier (${
        sorting.sortOrder === "asc" ? "croissant" : "décroissant"
      })`;
    }
    return "Trié par Date de création (décroissant)";
  }, [sorting]);

  // =====================================
  // FONCTIONS UTILITAIRES (INCHANGÉES)
  // =====================================

  // Fonction pour formater les nombres décimaux avec 2 décimales
  const formatDecimal = useCallback((value) => {
    if (value === null || value === undefined) return "";
    return Number(parseFloat(value).toFixed(2)).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  // Fonction pour calculer l'état du contrat
  const calculateStatus = useCallback((contract) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Vérifier si le contrat est vendu
    if (contract.remark && contract.remark.toLowerCase().includes("vendu")) {
      return "Contrat vendu";
    }

    if (contract.remark === "R" || contract.remark === "NR") {
      if (contract.closingDate && contract.creationDate) {
        const diffTime = Math.abs(
          new Date(contract.closingDate) - new Date(contract.creationDate)
        );
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `Rachat sous : ${diffDays} jour(s)`;
      }
    }

    if (contract.limitDate) {
      const limitDate = new Date(contract.limitDate);
      limitDate.setHours(0, 0, 0, 0);
      const diffTime = limitDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return "Dépassement d'échéance";
      } else {
        return `Reste : ${diffDays} jour(s) avant terme`;
      }
    }

    return contract.status || "État non défini";
  }, []);

  // Obtenir l'état du contrat
  const getEtatContrat = useCallback((contract) => {
    return contract.etatContrat || "Aucun";
  }, []);

  // Fonction getCellStyle pour les couleurs conditionnelles
  const getCellStyle = useCallback((contract, field) => {
    const styles = {};

    // Règle 0: Si "remark" contient "vendu", appliquer la couleur de fond verte
    if (contract.remark && contract.remark.toLowerCase().includes("vendu")) {
      const highlightFields = [
        "agency",
        "contractNumber",
        "jewelWeight",
        "cdvaWeight",
        "cddWeight",
        "status",
        "creationDate",
        "limitDate",
        "disbursementAmount",
        "expectedAmount",
        "receivedAmount",
        "remainingAmount",
        "lastPaymentDate",
        "phone1",
        "phone2",
        "client",
        "closingDate",
        "dossier",
        "backgroundColor",
        "etatContrat",
      ];

      if (highlightFields.includes(field)) {
        styles.backgroundColor = "#ffa0a0";
        styles.color = "#000000"; // Blanc par défaut pour tous les champs

        if (field === "status") {
          styles.backgroundColor = "#ffb69d";
          styles.color = "#070707";
          
        }

        if (field === "receivedAmount" && contract.receivedAmount > 0) {
          styles.backgroundColor = "#ceffb9";
          styles.color = "#000000"; // Noir pour le champ status
        }
      }
    }

    // Règle 1: Si "Montant Perçu" > 0, couleur de fond: #ffff00
    if (field === "receivedAmount" && contract.receivedAmount > 0) {
      styles.backgroundColor = "#f8f86b";
    }

    // Règle 2: Si "Reste" = 0, appliquer la couleur de fond #ffff00 à plusieurs cellules
    if (contract.remainingAmount === 0) {
      const highlightFields = [
        "agency",
        "contractNumber",
        "jewelWeight",
        "cdvaWeight",
        "cddWeight",
        "status",
        "creationDate",
        "limitDate",
        "disbursementAmount",
        "expectedAmount",
        "receivedAmount",
        "remainingAmount",
        "lastPaymentDate",
        "phone1",
        "phone2",
        "client",
        "closingDate",
        "dossier",
        "backgroundColor",
        "etatContrat",
      ];

      if (highlightFields.includes(field)) {
        styles.backgroundColor = "#f8f86b";
      }
    }

    // Règle 3: Si "Reste" < 0, appliquer la couleur de fond #ffc000 à plusieurs cellules
    if (contract.remainingAmount < 0) {
      const highlightFields = [
        "agency",
        "contractNumber",
        "jewelWeight",
        "cdvaWeight",
        "cddWeight",
        "status",
        "creationDate",
        "limitDate",
        "disbursementAmount",
        "expectedAmount",
        "receivedAmount",
        "remainingAmount",
        "lastPaymentDate",
        "phone1",
        "phone2",
        "client",
        "closingDate",
        "dossier",
        "backgroundColor",
        "etatContrat",
      ];

      if (highlightFields.includes(field)) {
        styles.backgroundColor = "#ffc000";
        if (field === "remainingAmount") {
          styles.color = "#ff0000";
        }
      }
    }

    // Règle 4: Si "Dernier Versement" = date du jour, texte en rouge
    if (field === "lastPaymentDate" && contract.lastPaymentDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const lastPayment = new Date(contract.lastPaymentDate);
      lastPayment.setHours(0, 0, 0, 0);

      if (lastPayment.toDateString() === today.toDateString()) {
        styles.color = "#ff0000";
        styles.fontWeight = "bold";
        styles.backgroundColor = "#FFFFCC";
      }
    }

    // Règle 5: Si "Remise" > 0, couleur de fond: #b6d7a8
    if (field === "discount" && contract.discount > 0) {
      styles.backgroundColor = "#b6d7a8";
    }

    // Règle 6: Si "Rmq" = "R", couleur de fond: #6FA8DC
    if (field === "remark" && contract.remark === "R") {
      styles.backgroundColor = "#6FA8DC";
    }

    return styles;
  }, []);

  // Style pour la ligne en fonction de l'état du contrat et de la couleur personnalisée
  const getRowStyle = useCallback((contract) => {
    if (contract.backgroundColor) {
      return { backgroundColor: contract.backgroundColor };
    }

    switch (contract.etatContrat) {
      case "A vendre":
        return { backgroundColor: "#64d5fe" };
      case "Vendu":
        return { backgroundColor: "#00FF00" };
      case "A surveiller":
        return { backgroundColor: "#ffd700" };
      case "Retard":
        return { backgroundColor: "#ff6868" };
      default:
        return {};
    }
  }, []);

  // =====================================
  // FONCTIONS DE NAVIGATION (MODIFIÉES pour inclure le tri)
  // =====================================

  // Fonction pour restaurer les positions de scroll
  const restoreScrollPositions = useCallback((state) => {
    if (state.scrollPosition) {
      window.scrollTo(0, state.scrollPosition);
    }

    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = state.tableScrollTop || 0;
      tableContainerRef.current.scrollLeft = state.tableScrollLeft || 0;
    }
  }, []);

  // 🆕 MODIFIÉE : Fonction pour sauvegarder l'état avant navigation (avec tri et statusFilter)
  const saveNavigationState = useCallback(
    (contractId) => {
      const scrollPosition = window.pageYOffset;
      const tableContainer = tableContainerRef.current;
      const tableScrollTop = tableContainer ? tableContainer.scrollTop : 0;
      const tableScrollLeft = tableContainer ? tableContainer.scrollLeft : 0;

      const navigationState = {
        contractId,
        selectedRow: contractId,
        scrollPosition,
        tableScrollTop,
        tableScrollLeft,
        filters: { ...filters }, // 🆕 MODIFICATION : Inclut maintenant statusFilter
        pagination: {
          page: pagination.page,
          limit: pagination.limit,
          total: pagination.total,
          pages: pagination.pages,
        },
        paginationEnabled, // 🆕 NOUVEAU
        contractsLength: contracts.length,
        timestamp: Date.now(),
        // 🆕 NOUVEAU : Sauvegarder l'état de tri
        sorting: { ...sorting },
      };

      sessionStorage.setItem(
        "contractListState",
        JSON.stringify(navigationState)
      );
      console.log("💾 État de navigation sauvegardé:", {
        contractId: navigationState.contractId,
        filtersCount: Object.keys(navigationState.filters).length,
        page: navigationState.pagination.page,
        scrollY: navigationState.scrollPosition,
        sorting: navigationState.sorting,
        statusFilter: navigationState.filters.statusFilter, // 🆕 NOUVEAU
      });
    },
    [filters, pagination, paginationEnabled, contracts.length, sorting]
  );

  // 🆕 MODIFIÉE : Fonction restoreNavigationState (pour inclure le tri et statusFilter)
  const restoreNavigationState = useCallback(() => {
    try {
      const savedState = sessionStorage.getItem("contractListState");
      if (savedState) {
        const state = JSON.parse(savedState);

        const isStateValid = Date.now() - state.timestamp < 300000;

        if (isStateValid && state.contractId) {
          console.log("🔄 Restauration de l'état:", {
            contractId: state.contractId,
            page: state.pagination.page,
            scrollY: state.scrollPosition,
            sorting: state.sorting,
            statusFilter: state.filters.statusFilter, // 🆕 NOUVEAU
          });

          // Marquer le début de restauration
          setIsRestoring(true);

          // Restaurer la sélection
          setSelectedRow(state.contractId);

          // 🆕 NOUVEAU : Restaurer l'état de tri
          if (state.sorting) {
            setSorting(state.sorting);
          }

          // 🆕 NOUVEAU : Restaurer l'état de la pagination
          if (state.paginationEnabled !== undefined) {
            setPaginationEnabled(state.paginationEnabled);
          }

          // Vérifier si on a besoin de changer les filtres/pagination
          const needsFilterUpdate =
            JSON.stringify(filters) !== JSON.stringify(state.filters);
          const needsPaginationUpdate =
            pagination.page !== state.pagination.page ||
            pagination.limit !== state.pagination.limit;
          const needsSortingUpdate =
            state.sorting &&
            (sorting.sortBy !== state.sorting.sortBy ||
              sorting.sortOrder !== state.sorting.sortOrder);

          if (
            needsFilterUpdate ||
            needsPaginationUpdate ||
            needsSortingUpdate
          ) {
            console.log("🔄 Mise à jour des filtres/pagination/tri nécessaire");

            // Restaurer filtres et pagination
            if (needsFilterUpdate) {
              setFilters(state.filters); // 🆕 MODIFICATION : Inclut maintenant statusFilter
            }

            if (needsPaginationUpdate) {
              setPagination((prev) => ({
                ...prev,
                page: state.pagination.page,
                limit: state.pagination.limit,
                total: state.pagination.total || prev.total,
                pages: state.pagination.pages || prev.pages,
              }));
            }

            // Programmer le scroll après rechargement
            setTimeout(() => {
              setScrollToContractId(state.contractId);
              setShouldScrollToContract(true);
              restoreScrollPositions(state);
              setIsRestoring(false);
              setRestorationComplete(true);
            }, 500);
          } else {
            // Pas de rechargement nécessaire, scroll direct
            console.log("📍 Scroll direct sans rechargement");
            setScrollToContractId(state.contractId);
            setShouldScrollToContract(true);
            restoreScrollPositions(state);
            setIsRestoring(false);
            setRestorationComplete(true);
          }

          sessionStorage.removeItem("contractListState");
          return true;
        }
      }
    } catch (error) {
      console.error("Erreur lors de la restauration:", error);
      setIsRestoring(false);
    }
    return false;
  }, [filters, pagination, sorting, restoreScrollPositions]);
  // =====================================
  // 🔧 CORRECTION: GESTIONNAIRES D'ÉVÉNEMENTS AVEC DOUBLE-CLIC FONCTIONNEL
  // =====================================

  // 🔧 CORRECTION: Gestionnaire de clic simple - AVEC DÉLAI POUR DOUBLE-CLIC
  const handleRowClick = useCallback((contractId, event) => {
    const now = Date.now();

    // Protection anti-spam
    if (now - lastClickTimeRef.current < clickThrottleMs) {
      if (event && event.preventDefault) event.preventDefault();
      if (event && event.stopPropagation) event.stopPropagation();
      return;
    }
    lastClickTimeRef.current = now;

    // Vérifier éléments interactifs
    if (event && event.target) {
      const target = event.target;
      const isInteractive =
        target.tagName === "SELECT" ||
        target.tagName === "A" ||
        target.tagName === "BUTTON" ||
        target.tagName === "INPUT" ||
        target.closest("button") ||
        target.closest("a") ||
        target.closest("select") ||
        target.closest("input") ||
        target.classList.contains("col-resizer");

      if (isInteractive) {
        return;
      }
    }

    // Empêcher le comportement par défaut
    if (event) {
      if (event.preventDefault) event.preventDefault();
      if (event.stopPropagation) event.stopPropagation();
      if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
        event.nativeEvent.stopImmediatePropagation();
      }
    }

    // Annuler le timeout précédent s'il existe
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    // 🔧 CORRECTION: Attendre pour vérifier si c'est un double-clic
    clickTimeoutRef.current = setTimeout(() => {
      // Si ce n'est pas un double-clic, traiter comme clic simple
      if (!isDoubleClickRef.current) {
        setSelectedRow((prevSelected) => {
          const newSelected = prevSelected === contractId ? null : contractId;
          console.log(
            `👆 Clic simple traité: ${contractId} (${
              newSelected ? "sélectionné" : "désélectionné"
            })`
          );
          return newSelected;
        });
      }

      // Reset du flag double-clic
      isDoubleClickRef.current = false;
      clickTimeoutRef.current = null;
    }, 250); // Délai pour détecter le double-clic
  }, []);

  // 🔧 CORRECTION: Gestionnaire de double-clic - PRIORITAIRE
  const handleRowDoubleClick = useCallback(
    (contractId, event) => {
      const now = Date.now();

      // Marquer qu'un double-clic est en cours
      isDoubleClickRef.current = true;

      // Annuler le timeout du clic simple
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }

      // Protection anti-spam pour double-clic
      if (now - lastDoubleClickTimeRef.current < doubleClickThrottleMs) {
        if (event && event.preventDefault) event.preventDefault();
        if (event && event.stopPropagation) event.stopPropagation();
        return;
      }
      lastDoubleClickTimeRef.current = now;

      // Vérifier éléments interactifs
      if (event && event.target) {
        const target = event.target;
        const isInteractive =
          target.tagName === "SELECT" ||
          target.tagName === "A" ||
          target.tagName === "BUTTON" ||
          target.tagName === "INPUT" ||
          target.closest("button") ||
          target.closest("a") ||
          target.closest("select") ||
          target.closest("input") ||
          target.classList.contains("col-resizer");

        if (isInteractive) {
          isDoubleClickRef.current = false; // Reset si on ne traite pas
          return;
        }
      }

      // Empêcher le comportement par défaut
      if (event) {
        if (event.preventDefault) event.preventDefault();
        if (event.stopPropagation) event.stopPropagation();
        if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
          event.nativeEvent.stopImmediatePropagation();
        }
      }

      console.log(`🖱️ DOUBLE-CLIC traité sur contrat: ${contractId}`);

      // Sélectionner immédiatement la ligne
      setSelectedRow(contractId);

      // Sauvegarder l'état AVANT navigation
      saveNavigationState(contractId);

      // Navigation immédiate vers la page détail
      console.log(`🚀 Navigation vers /contracts/${contractId}`);
      navigate(`/contracts/${contractId}`, {
        state: {
          fromContractList: true,
          contractIds: contracts.map((c) => c._id),
        },
      });

      // Reset du flag après un délai
      setTimeout(() => {
        isDoubleClickRef.current = false;
      }, 500);
    },
    [doubleClickThrottleMs, navigate, saveNavigationState, contracts]
  );

  // 🔧 CORRECTION: Nettoyage des timeouts à la destruction du composant
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  // =====================================
  // FONCTIONS DE MISE À JOUR ET HANDLERS (INCHANGÉES)
  // =====================================

  // Charger les totaux généraux
  const loadGeneralTotals = useCallback(async () => {
    try {
      setLoadingGeneralTotals(true);
      const cleanParams = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== undefined && value !== null) {
          cleanParams[key] = value;
        }
      });

      const result = await contractService.getTotals(cleanParams);
      if (result.success) {
        setGeneralTotals(result.data);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des totaux généraux:", error);
    } finally {
      setLoadingGeneralTotals(false);
    }
  }, [filters]);

  // Mettre à jour l'état du contrat
  const updateEtatContrat = useCallback(
    async (contract, newEtat) => {
      try {
        const updateData = {
          etatContrat: newEtat,
          backgroundColor: "",
        };

        const result = await contractService.updateContract(
          contract._id,
          updateData
        );

        if (result.success) {
          setContracts((prevContracts) =>
            prevContracts.map((c) =>
              c._id === contract._id
                ? {
                    ...c,
                    etatContrat: newEtat,
                    backgroundColor: "",
                    version: c.version + 1,
                  }
                : c
            )
          );
          loadGeneralTotals();
          setForceUpdate((prev) => !prev);
        }
      } catch (error) {
        console.error(
          "Erreur lors de la mise à jour de l'état du contrat:",
          error
        );
      }
    },
    [loadGeneralTotals]
  );

  // Mettre à jour la couleur de fond d'un contrat
  const updateBackgroundColor = useCallback(
    async (contract, color) => {
      try {
        const updateData = {
          backgroundColor: color,
          etatContrat: "Aucun",
        };

        const result = await contractService.updateContract(
          contract._id,
          updateData
        );

        if (result.success) {
          setContracts((prevContracts) =>
            prevContracts.map((c) =>
              c._id === contract._id
                ? {
                    ...c,
                    backgroundColor: color,
                    etatContrat: "Aucun",
                    version: c.version + 1,
                  }
                : c
            )
          );
          loadGeneralTotals();
          setForceUpdate((prev) => !prev);
        }
      } catch (error) {
        console.error(
          "Erreur lors de la mise à jour de la couleur de fond:",
          error
        );
      }
    },
    [loadGeneralTotals]
  );

  // Handlers d'événements
  const handlePageChange = useCallback((page) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  // 🆕 MODIFICATION : handleFilterChange pour inclure le nouveau paramètre statusFilter
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    // 🆕 NOUVEAU : Remettre le tri par défaut lors d'un changement de filtres
    setSorting({
      sortBy: "creationDate",
      sortOrder: "desc",
    });
  }, []);

  const handleZoomChange = useCallback((e) => {
    setTableZoom(parseInt(e.target.value));
  }, []);

  // Props mémorisées pour optimiser les performances
  // =====================================
  // GESTION DU DRAG & DROP DES PHOTOS
  // =====================================
  const handleDragEnterRow = useCallback((e, contractId) => {
    e.preventDefault();
    e.stopPropagation();
    if (canManagePhotos) {
      setDragOverRowId(contractId);
    }
  }, [canManagePhotos]);

  const handleDragOverRow = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeaveRow = useCallback((e, contractId) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (dragOverRowId === contractId) {
      setDragOverRowId(null);
    }
  }, [dragOverRowId]);

  const handleDropRow = useCallback(async (e, contractId, photosCount) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverRowId(null);

    if (!canManagePhotos) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (!imageFile) {
      alert("Veuillez déposer une image");
      return;
    }

    const validation = photoService.validateImageFile(imageFile);
    if (!validation.isValid) {
      alert(validation.errors.join("\\n"));
      return;
    }

    setUploadingRowId(contractId);
    try {
      const isFirstPhoto = photosCount === 0;
      const result = await photoService.uploadPhoto(
        contractId,
        imageFile,
        "",
        isFirstPhoto
      );

      if (result.success) {
        contractService.invalidateContractCache(contractId);
        // Mettre à jour la ligne locale
        setContracts((prevContracts) =>
          prevContracts.map((c) => {
            if (c._id === contractId) {
              return { ...c, jewelPhotos: [...(c.jewelPhotos || []), result.data] };
            }
            return c;
          })
        );
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Erreur upload drag & drop:", error);
      alert("Erreur lors de l'upload de la photo: " + error.message);
    } finally {
      setUploadingRowId(null);
    }
  }, [canManagePhotos]);

  const memoizedProps = useMemo(
    () => ({
      colorColumnExpanded,
      stateColumnExpanded,
      getRowStyle,
      getCellStyle,
      calculateStatus,
      getEtatContrat,
      updateEtatContrat,
      updateBackgroundColor,
      formatDecimal,
      handleRowClick,
      handleRowDoubleClick,
      saveNavigationState,
      navigate,
      contractIds: contracts.map((c) => c._id),
      dragOverRowId,
      uploadingRowId,
      handleDragEnterRow,
      handleDragLeaveRow,
      handleDragOverRow,
      handleDropRow,
    }),
    [
      colorColumnExpanded,
      stateColumnExpanded,
      getRowStyle,
      getCellStyle,
      calculateStatus,
      getEtatContrat,
      updateEtatContrat,
      updateBackgroundColor,
      formatDecimal,
      handleRowClick,
      handleRowDoubleClick,
      saveNavigationState,
      navigate,
      contracts,
      dragOverRowId,
      uploadingRowId,
      handleDragEnterRow,
      handleDragLeaveRow,
      handleDragOverRow,
      handleDropRow,
    ]
  );

  // =====================================
  // USEEFFECTS (MODIFIÉS pour le tri côté serveur et statusFilter)
  // =====================================

  // Effect pour détecter le retour - INCHANGÉ
  useEffect(() => {
    const isReturningFromDetail = location.state?.fromContractDetail;

    if (isReturningFromDetail && !restorationComplete) {
      console.log("🔄 Détection du retour depuis la page de détail");

      // Bloquer temporairement les scrolls pendant la restauration
      document.body.style.overflow = "hidden";

      const restored = restoreNavigationState();
      if (restored) {
        console.log("✅ État restauré avec succès");
        // Débloquer le scroll après un délai
        setTimeout(() => {
          document.body.style.overflow = "";
        }, 500);
      } else {
        // Débloquer immédiatement si pas de restauration
        document.body.style.overflow = "";
      }
    }
  }, [location.state, restoreNavigationState, restorationComplete]);

  // Effect pour le scroll - INCHANGÉ
  useEffect(() => {
    // UNIQUEMENT pour la restauration après retour d'une page de détail
    if (shouldScrollToContract && scrollToContractId && contracts.length > 0) {
      const scrollToContract = () => {
        // Utiliser requestAnimationFrame pour optimiser le timing
        requestAnimationFrame(() => {
          const contractRow = document.querySelector(
            `tr[data-contract-id="${scrollToContractId}"]`
          );

          if (contractRow) {
            // Mesurer la position actuelle pour éviter les scrolls inutiles
            const rect = contractRow.getBoundingClientRect();
            const isVisible =
              rect.top >= 0 && rect.bottom <= window.innerHeight;

            if (!isVisible) {
              // Seulement scroller si l'élément n'est pas visible
              contractRow.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
              });
            }

            // Ajouter l'effet flash sans interférer avec le scroll
            setTimeout(
              () => {
                contractRow.classList.add("flash-highlight");
                setTimeout(() => {
                  contractRow.classList.remove("flash-highlight");
                }, 2000);
              },
              isVisible ? 0 : 300
            ); // Délai uniquement si on a scrollé

            console.log(
              "📍 Scroll de restauration effectué vers le contrat:",
              scrollToContractId,
              "Était visible:",
              isVisible
            );
          } else {
            console.log(
              "⚠️ Contrat non trouvé pour la restauration:",
              scrollToContractId
            );
          }
        });
      };

      // Délai minimal pour s'assurer que le DOM est prêt
      const timeoutId = setTimeout(scrollToContract, 100);

      // Réinitialiser les flags IMMÉDIATEMENT pour éviter les conflits
      setShouldScrollToContract(false);
      setScrollToContractId(null);

      return () => clearTimeout(timeoutId);
    }
  }, [shouldScrollToContract, scrollToContractId, contracts]);

  // Effect pour nettoyer les états de restauration
  useEffect(() => {
    if (restorationComplete) {
      const cleanup = setTimeout(() => {
        setRestorationComplete(false);
        console.log("🧹 Nettoyage des états de restauration terminé");
      }, 1000);

      return () => clearTimeout(cleanup);
    }
  }, [restorationComplete]);

  // 🆕 MODIFICATION : useEffect pour calculer les totaux (utilise les contrats reçus du serveur)
  useEffect(() => {
    if (contracts && contracts.length > 0) {
      const newTotals = contracts.reduce(
        (acc, contract) => ({
          jewelWeight:
            acc.jewelWeight + (parseFloat(contract.jewelWeight) || 0),
          cdvaWeight: acc.cdvaWeight + (parseFloat(contract.cdvaWeight) || 0),
          cddWeight: acc.cddWeight + (parseFloat(contract.cddWeight) || 0),
          disbursementAmount:
            acc.disbursementAmount +
            (parseFloat(contract.disbursementAmount) || 0),
          expectedAmount:
            acc.expectedAmount + (parseFloat(contract.expectedAmount) || 0),
          receivedAmount:
            acc.receivedAmount + (parseFloat(contract.receivedAmount) || 0),
          remainingAmount:
            acc.remainingAmount + (parseFloat(contract.remainingAmount) || 0),
          discount: acc.discount + (parseFloat(contract.discount) || 0),
        }),
        {
          jewelWeight: 0,
          cdvaWeight: 0,
          cddWeight: 0,
          disbursementAmount: 0,
          expectedAmount: 0,
          receivedAmount: 0,
          remainingAmount: 0,
          discount: 0,
        }
      );

      newTotals.contractCount = contracts.length;
      setTotals(newTotals);
    }
  }, [contracts]);

  // useEffect pour charger les totaux généraux (OPTIMISÉ)
  useEffect(() => {
    const filtersChanged =
      JSON.stringify(prevFilters) !== JSON.stringify(filters);
    if (filtersChanged && !isRestoring) {
      console.log("📊 Chargement des totaux généraux (filtersChanged)");
      loadGeneralTotals();
    }
  }, [filters, prevFilters, loadGeneralTotals, isRestoring]);

  // useEffect pour les colonnes redimensionnables
  useEffect(() => {
    if (!tableContainerRef.current || loading) return;

    const table = tableContainerRef.current.querySelector("table");
    const headers = table.querySelectorAll("th:not(.fixed-column)");

    headers.forEach((header) => {
      const existingResizer = header.querySelector(".col-resizer");
      if (existingResizer) {
        existingResizer.remove();
      }

      const resizer = document.createElement("div");
      resizer.className = "col-resizer";
      header.appendChild(resizer);

      let startX, startWidth;

      const startResize = (e) => {
        startX = e.pageX;
        startWidth = header.offsetWidth;
        document.addEventListener("mousemove", resize);
        document.addEventListener("mouseup", stopResize);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
      };

      const resize = (e) => {
        const width = startWidth + (e.pageX - startX);
        if (width > 50) {
          header.style.width = `${width}px`;
          header.style.minWidth = `${width}px`;
        }
      };

      const stopResize = () => {
        document.removeEventListener("mousemove", resize);
        document.removeEventListener("mouseup", stopResize);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };

      resizer.addEventListener("mousedown", startResize);
    });

    return () => {
      headers.forEach((header) => {
        const resizer = header.querySelector(".col-resizer");
        if (resizer) {
          const newResizer = resizer.cloneNode(true);
          resizer.parentNode.replaceChild(newResizer, resizer);
        }
      });
    };
  }, [contracts, loading]);

  // 🆕 MODIFICATION : useEffect pour charger les contrats (avec tri côté serveur et statusFilter)
  useEffect(() => {
    const filtersChanged =
      JSON.stringify(prevFilters) !== JSON.stringify(filters);
    const pageChanged = prevPage !== pagination.page;
    const limitChanged = prevLimit !== pagination.limit;
    const sortingChanged =
      JSON.stringify(prevSorting) !== JSON.stringify(sorting);
    const paginationEnabledChanged = prevPaginationEnabled !== paginationEnabled; // 🆕 NOUVEAU

    if (
      !filtersChanged &&
      !pageChanged &&
      !limitChanged &&
      !sortingChanged &&
      !paginationEnabledChanged && // 🆕 NOUVEAU
      contracts.length > 0 &&
      !forceUpdate
    ) {
      return;
    }

    // Si on est en cours de restauration, permettre le rechargement mais avec log
    if (isRestoring) {
      console.log("🔄 Rechargement des contrats pendant la restauration");
    }

    const fetchContracts = async () => {
      try {
        if (!isRestoring) {
          setLoading(true);
        }

        const cleanParams = {};
        Object.entries({
          ...filters, // 🆕 MODIFICATION : Inclut maintenant statusFilter
          page: paginationEnabled ? pagination.page : 1,
          limit: paginationEnabled ? pagination.limit : 100000,
          // 🆕 NOUVEAU : Ajouter les paramètres de tri
          sortBy: sorting.sortBy,
          sortOrder: sorting.sortOrder,
        }).forEach(([key, value]) => {
          if (value !== "" && value !== undefined && value !== null) {
            cleanParams[key] = value;
          }
        });

        console.log("🔍 Paramètres de requête:", cleanParams);

        const result = await contractService.getContracts(cleanParams);

        if (result.success) {
          setContracts(result.data);
          setPagination((prev) => ({
            ...prev,
            total: result.pagination?.total || 0,
            pages: result.pagination?.pages || 0,
          }));

          // 🆕 NOUVEAU : Log du tri reçu du serveur
          if (result.sorting) {
            console.log("✅ Tri confirmé par le serveur:", result.sorting);
          }

          // 🆕 NOUVEAU : Log du filtre de statut reçu du serveur
          if (result.statusFilter) {
            console.log(
              "✅ Filtre de statut confirmé par le serveur:",
              result.statusFilter
            );
          }

          // Si on était en restauration, permettre le scroll après chargement
          if (isRestoring && scrollToContractId) {
            setTimeout(() => {
              setShouldScrollToContract(true);
            }, 100);
          }
        } else {
          setError("Erreur lors du chargement des contrats.");
        }
      } catch (error) {
        console.error("Erreur lors du chargement des contrats:", error);
        setError("Erreur lors du chargement des contrats.");
      } finally {
        if (!isRestoring) {
          setLoading(false);
        }
      }
    };

    fetchContracts();
  }, [
    filters, // 🆕 MODIFICATION : Inclut maintenant statusFilter
    pagination.page,
    pagination.limit,
    paginationEnabled, // 🆕 NOUVEAU
    prevPaginationEnabled, // 🆕 NOUVEAU
    sorting, // 🆕 NOUVEAU : Ajouter sorting aux dépendances
    prevFilters,
    prevPage,
    prevLimit,
    prevSorting, // 🆕 NOUVEAU : Ajouter prevSorting aux dépendances
    contracts.length,
    forceUpdate,
    isRestoring,
    scrollToContractId,
  ]);

  // =====================================
  // COMPOSANT MEMOIZEDTABLEROW (INCHANGÉ)
  // =====================================

  const MemoizedTableRow = React.memo(
    ({
      contract,
      rowNumber,
      colorColumnExpanded,
      stateColumnExpanded,
      getRowStyle,
      getCellStyle,
      calculateStatus,
      getEtatContrat,
      updateEtatContrat,
      updateBackgroundColor,
      formatDecimal,
      formatDate,
      formatAmount,
      isHovered,
      isSelected,
      onMouseEnter,
      onMouseLeave,
      onClick,
      onDoubleClick,
      saveNavigationState,
      navigate,
      contractIds,
      dragOverRowId,
      uploadingRowId,
      handleDragEnterRow,
      handleDragLeaveRow,
      handleDragOverRow,
      handleDropRow,
    }) => {
      const isDraggedOver = dragOverRowId === contract._id;
      const isUploading = uploadingRowId === contract._id;

      const baseRowStyle = getRowStyle(contract);

      const rowStyle = {
        ...baseRowStyle,
        ...(isHovered &&
        !contract.backgroundColor &&
        contract.etatContrat === "Aucun"
          ? { backgroundColor: "#e5e7eb" }
          : {}),
        ...(isSelected
          ? {
              outline: "3px solid #3b82f6",
              outlineOffset: "-3px",
              backgroundColor: "#dbeafe",
              boxShadow: "0 0 0 1px #3b82f6",
            }
          : {}),
        ...(isDraggedOver
          ? {
              outline: "2px dashed #3b82f6",
              outlineOffset: "-2px",
              backgroundColor: "#eff6ff",
            }
          : {}),
        transition: "all 0.15s ease",
        cursor: "pointer",
        opacity: isUploading ? 0.6 : 1,
      };

      const getFixedColumnStyle = (field = null) => {
        const conditionalStyle = getCellStyle(contract, field);
        if (conditionalStyle.backgroundColor && !isSelected && !isDraggedOver) {
          return conditionalStyle;
        }

        if (isDraggedOver) {
          return {
            backgroundColor: "#eff6ff",
            borderColor: "#3b82f6",
          };
        }

        if (isSelected) {
          return {
            backgroundColor: "#dbeafe",
            borderColor: "#3b82f6",
          };
        }

        if (contract.backgroundColor) {
          return { backgroundColor: contract.backgroundColor };
        }

        if (contract.etatContrat && contract.etatContrat !== "Aucun") {
          return baseRowStyle;
        }

        if (isHovered) {
          return { backgroundColor: "#e5e7eb" };
        }

        return { backgroundColor: "#ffffff" };
      };

      // Gestionnaire pour le lien Détails
      const handleDetailsClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        console.log(`🔗 Clic lien Détails pour: ${contract._id}`);

        // Sauvegarder l'état
        saveNavigationState(contract._id);

        // Navigation
        navigate(`/contracts/${contract._id}`, {
          state: { fromContractList: true, contractIds },
        });
      };

      return (
        <tr
          style={rowStyle}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          onDragEnter={(e) => handleDragEnterRow(e, contract._id)}
          onDragOver={handleDragOverRow}
          onDragLeave={(e) => handleDragLeaveRow(e, contract._id)}
          onDrop={(e) => handleDropRow(e, contract._id, contract.jewelPhotos?.length || 0)}
          className="hover-effect cursor-pointer"
          role="row"
          aria-selected={isSelected}
          title="Double-cliquez pour ouvrir les détails du contrat. Glissez-déposez une image pour l'ajouter."
          data-contract-id={contract._id}
        >
          <td
            className="fixed-column fixed-column-1"
            style={getFixedColumnStyle()}
          >
            {rowNumber}
          </td>

          <td
            className={`fixed-column fixed-column-2 ${
              !colorColumnExpanded ? "column-collapsed" : ""
            }`}
            style={getFixedColumnStyle("backgroundColor")}
          >
            {colorColumnExpanded ? (
              <select
                value={contract.backgroundColor || ""}
                onChange={(e) =>
                  updateBackgroundColor(contract, e.target.value)
                }
                className="block w-full text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                onClick={(e) => e.stopPropagation()}
              >
                <option value="">-</option>
                <option value="#FFB6C1" style={{ backgroundColor: "#FFB6C1" }}>
                  Rose
                </option>
                <option value="#ADD8E6" style={{ backgroundColor: "#ADD8E6" }}>
                  Bleu clair
                </option>
                <option value="#90EE90" style={{ backgroundColor: "#90EE90" }}>
                  Vert clair
                </option>
                <option value="#FFFFE0" style={{ backgroundColor: "#FFFFE0" }}>
                  Jaune clair
                </option>
                <option value="#E6E6FA" style={{ backgroundColor: "#E6E6FA" }}>
                  Lavande
                </option>
                <option value="#FFD700" style={{ backgroundColor: "#FFD700" }}>
                  Or
                </option>
                <option value="#FFA07A" style={{ backgroundColor: "#FFA07A" }}>
                  Saumon
                </option>
                <option value="#D3D3D3" style={{ backgroundColor: "#D3D3D3" }}>
                  Gris clair
                </option>
              </select>
            ) : contract.backgroundColor ? (
              "•"
            ) : (
              "-"
            )}
          </td>

          <td
            className={`fixed-column fixed-column-3 ${
              !stateColumnExpanded ? "column-collapsed" : ""
            }`}
            style={getFixedColumnStyle("etatContrat")}
          >
            {stateColumnExpanded ? (
              <select
                value={getEtatContrat(contract)}
                onChange={(e) => updateEtatContrat(contract, e.target.value)}
                className="block w-full text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: "transparent",
                  ...(() => {
                    switch (contract.etatContrat) {
                      case "A vendre":
                        return {
                          color: "black",
                          fontWeight: "bold",
                          border: "1px solid #68a2ff",
                        };
                      case "Vendu":
                        return {
                          color: "black",
                          fontWeight: "bold",
                          border: "1px solid #00FF00",
                        };
                      case "A surveiller":
                        return {
                          color: "black",
                          fontWeight: "bold",
                          border: "1px solid #ffd700",
                        };
                      case "Retard":
                        return {
                          color: "white",
                          fontWeight: "bold",
                          border: "1px solid #ff6868",
                        };
                      default:
                        return {};
                    }
                  })(),
                }}
              >
                <option value="Aucun">-</option>
                <option value="A vendre">A vendre</option>
                <option value="Vendu">Vendu</option>
                <option value="A surveiller">A surveiller</option>
                <option value="Retard">Retard</option>
              </select>
            ) : contract.etatContrat === "A vendre" ? (
              "V"
            ) : contract.etatContrat === "Vendu" ? (
              "✓"
            ) : contract.etatContrat === "A surveiller" ? (
              "S"
            ) : contract.etatContrat === "Retard" ? (
              "R"
            ) : (
              "-"
            )}
          </td>

          <td
            className="fixed-column fixed-column-4"
            style={getFixedColumnStyle("contractNumber")}
          >
            {contract.contractNumber}
          </td>

          <td
            className="text-right"
            style={getCellStyle(contract, "jewelWeight")}
          >
            {formatDecimal(contract.jewelWeight)}
          </td>
          <td
            className="text-right"
            style={getCellStyle(contract, "cdvaWeight")}
          >
            {formatDecimal(contract.cdvaWeight)}
          </td>
          <td
            className="text-right"
            style={getCellStyle(contract, "cddWeight")}
          >
            {formatDecimal(contract.cddWeight)}
          </td>
          <td style={getCellStyle(contract, "status")}>
            {calculateStatus(contract)}
          </td>
          <td style={getCellStyle(contract, "creationDate")}>
            {formatDate(contract.creationDate)}
          </td>
          <td style={getCellStyle(contract, "limitDate")}>
            {formatDate(contract.limitDate)}
          </td>
          <td
            className="text-right"
            style={getCellStyle(contract, "disbursementAmount")}
          >
            {formatAmount(contract.disbursementAmount)}
          </td>
          <td
            className="text-right"
            style={getCellStyle(contract, "expectedAmount")}
          >
            {formatAmount(contract.expectedAmount)}
          </td>
          <td
            className="text-right"
            style={getCellStyle(contract, "receivedAmount")}
          >
            {formatAmount(contract.receivedAmount)}
          </td>
          <td
            className="text-right"
            style={getCellStyle(contract, "remainingAmount")}
          >
            {formatAmount(contract.remainingAmount)}
          </td>
          <td style={getCellStyle(contract, "lastPaymentDate")}>
            {formatDate(contract.lastPaymentDate)}
          </td>
          <td style={getCellStyle(contract, "phone1")}>{contract.phone1}</td>
          <td 
            style={{
              ...getCellStyle(contract, "client"),
              ...(contract.jewelPhotos?.length > 0 ? {
                fontWeight: "bold",
                color: "#166534" // Vert foncé
              } : {})
            }}
          >
            {contract.client}
          </td>
          <td style={getCellStyle(contract, "closingDate")}>
            {formatDate(contract.closingDate)}
          </td>
          <td className="text-right" style={getCellStyle(contract, "discount")}>
            {contract.discount > 0 ? formatAmount(contract.discount) : ""}
          </td>
          <td className="text-center" style={getCellStyle(contract, "remark")}>
            {contract.remark}
          </td>

          <td>
            <Link
              to={`/contracts/${contract._id}`}
              state={{ fromContractList: true }}
              className="text-primary-600 hover:text-primary-900 underline"
              onClick={handleDetailsClick}
            >
              Détails
            </Link>
          </td>
        </tr>
      );
    },
    (prevProps, nextProps) => {
      const prevIsDraggedOver = prevProps.dragOverRowId === prevProps.contract._id;
      const nextIsDraggedOver = nextProps.dragOverRowId === nextProps.contract._id;
      
      const prevIsUploading = prevProps.uploadingRowId === prevProps.contract._id;
      const nextIsUploading = nextProps.uploadingRowId === nextProps.contract._id;

      return (
        prevProps.contract._id === nextProps.contract._id &&
        prevProps.contract.version === nextProps.contract.version &&
        prevProps.contract.jewelPhotos?.length === nextProps.contract.jewelPhotos?.length &&
        prevProps.rowNumber === nextProps.rowNumber &&
        prevProps.colorColumnExpanded === nextProps.colorColumnExpanded &&
        prevProps.stateColumnExpanded === nextProps.stateColumnExpanded &&
        prevProps.isHovered === nextProps.isHovered &&
        prevProps.isSelected === nextProps.isSelected &&
        prevIsDraggedOver === nextIsDraggedOver &&
        prevIsUploading === nextIsUploading
      );
    }
  );

  // 🆕 NOUVELLES FONCTIONS : Gestionnaires d'export (à ajouter avant le rendu JSX)

  // Fonction pour exporter en Excel
  const handleExportExcel = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportStatus("excel");

      console.log("📊 Début export Excel...");
      
      // 🆕 NOUVEAU: Récupérer TOUS les contrats correspondants aux filtres pour l'export
      const exportParams = { ...filters, limit: 100000, page: 1 };
      if (sorting && sorting.sortBy) {
        exportParams.sortBy = sorting.sortBy;
        exportParams.sortOrder = sorting.sortOrder;
      }
      
      const response = await contractService.getContracts(exportParams);
      const contractsToExport = response && response.success ? response.data : contracts;

      console.log("Contrats à exporter:", contractsToExport.length);
      console.log("Filtres appliqués:", filters);

      const result = exportService.exportToExcel(contractsToExport, filters);

      if (result.success) {
        console.log("✅ Export Excel réussi:", result.fileName);
        // Notification de succès (optionnel)
        setTimeout(() => {
          alert(`✅ Export Excel terminé !\nFichier: ${result.fileName}`);
        }, 500);
      } else {
        console.error("❌ Erreur export Excel:", result.error);
        alert(`❌ Erreur lors de l'export Excel:\n${result.error}`);
      }
    } catch (error) {
      console.error("❌ Erreur export Excel:", error);
      alert(`❌ Erreur lors de l'export Excel:\n${error.message}`);
    } finally {
      setIsExporting(false);
      setExportStatus(null);
    }
  }, [contracts, filters, sorting]);

  // Fonction pour exporter en PDF
  const handleExportPDF = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportStatus("pdf");

      console.log("🖨️ Début export PDF...");
      
      // 🆕 NOUVEAU: Récupérer TOUS les contrats correspondants aux filtres pour l'export
      const exportParams = { ...filters, limit: 100000, page: 1 };
      if (sorting && sorting.sortBy) {
        exportParams.sortBy = sorting.sortBy;
        exportParams.sortOrder = sorting.sortOrder;
      }
      
      const response = await contractService.getContracts(exportParams);
      const contractsToExport = response && response.success ? response.data : contracts;

      console.log("Contrats à exporter:", contractsToExport.length);
      console.log("Filtres appliqués:", filters);
      console.log("Totaux à inclure:", totals);

      const result = exportService.exportToPDF(contractsToExport, filters, totals);

      if (result.success) {
        console.log("✅ Export PDF réussi:", result.fileName);
        // Notification de succès (optionnel)
        setTimeout(() => {
          alert(`✅ Export PDF terminé !\nFichier: ${result.fileName}`);
        }, 500);
      } else {
        
        console.error("❌ Erreur export PDF:", result.error);
        alert(`❌ Erreur lors de l'export PDF:\n${result.error}`);
      }
    } catch (error) {
      console.error("❌ Erreur export PDF:", error);
      alert(`❌ Erreur lors de l'export PDF:\n${error.message}`);
    } finally {
      setIsExporting(false);
      setExportStatus(null);
    }
  }, [contracts, filters, totals, sorting]);

  // =====================================
  // RENDU JSX AVEC TRI CÔTÉ SERVEUR ET FILTRAGE AVANCÉ
  // =====================================

  return (
    <div className="content-with-sidebar">
      <style jsx="true">{`
        .content-with-sidebar {
          margin-left: 16rem;
          transition: margin-left 0.3s ease;
          min-height: 100vh;
          background-color: #f9fafb;
        }

        .table-container {
          position: relative;
          overflow-x: auto;
          overflow-y: auto;
          max-height: calc(100vh - 250px);
          border: 1px solid #e2e8f0;
          border-radius: 0.375rem;
          background-color: white;
          scroll-behavior: auto !important;
        }

        .table-container.fullscreen-table {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          max-height: 100vh !important;
          z-index: 1000 !important;
          border-radius: 0 !important;
          border: none !important;
          margin: 0 !important;
          padding-bottom: 5rem !important;
        }

        .table-container * {
          scroll-behavior: auto !important;
        }

        .resizable-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: ${tableZoom}%;
        }

        .resizable-table th {
          position: sticky;
          top: 0;
          background-color: #f9fafb;
          z-index: 10;
          white-space: nowrap;
          font-weight: 600;
          border-bottom: 2px solid #e2e8f0;
        }

        .sort-header {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s ease;
          padding: 0.5rem 1rem;
        }

        .sort-header:hover {
          background-color: #e2e8f0;
        }

        .sort-header-content {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.25rem;
        }

        .sort-icon {
          margin-left: 0.25rem;
          font-size: 0.875rem;
          transition: color 0.2s ease;
        }

        .resizable-table td {
          padding: 0.3rem 1rem;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
          outline: none !important;
          scroll-margin: 0 !important;
        }

        .resizable-table td:focus {
          outline: none !important;
          box-shadow: none !important;
        }

        .col-resizer {
          position: absolute;
          top: 0;
          right: 0;
          width: 5px;
          height: 100%;
          background-color: #e2e8f0;
          cursor: col-resize;
          user-select: none;
          touch-action: none;
        }

        .col-resizer:hover {
          background-color: #3b82f6;
        }

        .etat-contrat-col {
          width: auto;
          min-width: ${stateColumnExpanded ? "120px" : "30px"};
          max-width: ${stateColumnExpanded ? "none" : "30px"};
          transition: min-width 0.3s, max-width 0.3s;
        }

        .fixed-column {
          position: sticky;
          left: 0;
          z-index: 20;
        }

        .couleur-ligne {
          width: auto;
          min-width: ${colorColumnExpanded ? "80px" : "30px"};
          max-width: ${colorColumnExpanded ? "none" : "30px"};
          transition: min-width 0.3s, max-width 0.3s;
          overflow: hidden;
        }

        .column-collapsed {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .fixed-column-1 {
          left: 0;
          z-index: 25;
          width: 50px;
          min-width: 50px;
          text-align: center;
        }

        .fixed-column-2 {
          left: 50px;
          z-index: 24;
          transition: left 0.3s;
        }

        .fixed-column-3 {
          left: ${colorColumnExpanded ? "130px" : "80px"};
          z-index: 23;
          transition: left 0.3s;
        }

        .fixed-column-4 {
          left: ${colorColumnExpanded && stateColumnExpanded
            ? "250px"
            : colorColumnExpanded && !stateColumnExpanded
            ? "160px"
            : !colorColumnExpanded && stateColumnExpanded
            ? "200px"
            : "110px"};
          z-index: 22;
          transition: left 0.3s;
        }

        th.fixed-column {
          background-color: #f9fafb !important;
          z-index: 30;
        }

        .totals-row td {
          font-weight: bold;
          background-color: #f1f5f9;
          border-top: 2px solid #cbd5e1;
        }

        .totals-row-global td {
          font-weight: bold;
          background-color: #e2e8f0;
          border-top: 2px solid #94a3b8;
          border-bottom: 2px solid #94a3b8;
        }

        .resizable-table tr {
          user-select: none;
          outline: none !important;
          scroll-behavior: auto !important;
          scroll-margin: 0 !important;
          scroll-snap-align: none !important;
        }

        .resizable-table tr:focus {
          outline: none !important;
          box-shadow: none !important;
        }

        .resizable-table tr:focus-visible {
          outline: none !important;
        }

        .resizable-table tr.hover-effect {
          transition: background-color 0.1s ease;
          scroll-margin: 0 !important;
          scroll-snap-align: none !important;
        }

        .resizable-table tr.hover-effect:active {
          transform: none;
          scroll-behavior: auto !important;
        }

        .resizable-table tr.hover-effect:focus {
          outline: none !important;
          scroll-behavior: auto !important;
        }

        .resizable-table td button,
        .resizable-table td a,
        .resizable-table td select,
        .resizable-table td input {
          outline: none;
          display: inline-block;
          text-decoration: underline;
          background: transparent;
          border: none;
          color: #2563eb;
          cursor: pointer;
          padding: 0;
          font-size: inherit;
          scroll-margin: 0 !important;
          scroll-behavior: auto !important;
        }

        .resizable-table td button:hover,
        .resizable-table td a:hover {
          color: #1d4ed8;
        }

        .resizable-table td button:focus,
        .resizable-table td a:focus,
        .resizable-table td select:focus,
        .resizable-table td input:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
          border-radius: 2px;
          scroll-behavior: auto !important;
        }

        .resizable-table tr[aria-selected="true"] {
          position: relative;
          z-index: 5;
          scroll-margin-top: 0 !important;
          scroll-margin: 0 !important;
          background-color: #dbeafe !important;
          outline: 3px solid #3b82f6 !important;
          outline-offset: -3px;
          contain: layout;
          scroll-behavior: auto !important;
        }

        .resizable-table tr[aria-selected="true"] td {
          background-color: #dbeafe !important;
          border-color: #3b82f6 !important;
          scroll-margin: 0 !important;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .totals-row:hover td,
        .totals-row-global:hover td {
          background-color: inherit !important;
        }

        .flash-highlight {
          animation: flash-effect 2s ease-in-out;
          scroll-margin-top: 0 !important;
          scroll-margin: 0 !important;
          contain: layout;
          will-change: background-color;
          scroll-behavior: auto !important;
        }

        @keyframes flash-effect {
          0% {
            background-color: #fef3c7;
            box-shadow: 0 0 20px rgba(245, 158, 11, 0.5);
            transform: none;
          }
          25% {
            background-color: #fde047;
            box-shadow: 0 0 30px rgba(245, 158, 11, 0.7);
            transform: none;
          }
          50% {
            background-color: #facc15;
            box-shadow: 0 0 25px rgba(245, 158, 11, 0.6);
            transform: none;
          }
          75% {
            background-color: #fde047;
            box-shadow: 0 0 15px rgba(245, 158, 11, 0.4);
            transform: none;
          }
          100% {
            background-color: transparent;
            box-shadow: none;
            transform: none;
          }
        }

        @keyframes pulse-selection {
          0% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
            transform: none;
          }
          50% {
            box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
            transform: none;
          }
          100% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
            transform: none;
          }
        }

        .resizable-table tr[aria-selected="true"] {
          animation: pulse-selection 0.4s ease-out;
          scroll-behavior: auto !important;
        }

        /* 🆕 NOUVEAU : Responsive pour les icônes de tri et en-têtes */
        @media (max-width: 768px) {
          .sort-header-content {
            flex-direction: column;
            align-items: center;
            padding: 0.25rem 0.5rem;
            gap: 0.125rem;
          }

          .sort-icon {
            margin-left: 0;
            margin-top: 0.125rem;
          }

          .resizable-table tr,
          .resizable-table td,
          .resizable-table th {
            scroll-margin: 0 !important;
            scroll-behavior: auto !important;
          }

          .resizable-table tr[aria-selected="true"] {
            outline: 2px solid #3b82f6;
            outline-offset: -2px;
            scroll-margin: 0 !important;
          }

          .flash-highlight {
            animation: flash-effect-mobile 1.5s ease-in-out;
            scroll-margin: 0 !important;
          }

          @keyframes flash-effect-mobile {
            0%,
            100% {
              background-color: transparent;
              border: none;
              transform: none;
            }
            50% {
              background-color: #fde047;
              border: 2px solid #f59e0b;
              transform: none;
            }
          }
        }

        @media (max-width: 480px) {
          .sort-header {
            padding: 0.25rem 0.5rem;
          }

          .sort-header-content {
            font-size: 0.75rem;
          }

          .sort-icon {
            font-size: 0.75rem;
          }
        }
      `}</style>

      <FilterPanel
        onFilterChange={handleFilterChange}
        initialFilters={filters}
        totals={totals}
        generalTotals={generalTotals}
        loadingGeneralTotals={loadingGeneralTotals}
        isRestoring={isRestoring}
      />

      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Liste des contrats</h1>

        {loading ? (
          <LoadingSpinner message="Chargement des contrats..." />
        ) : error ? (
          <div className="bg-red-100 p-4 rounded-md text-red-700 mb-4">
            {error}
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-col gap-2">
                <div>
                  <span className="font-medium">{contracts.length}</span>{" "}
                  contrat(s) trouvé(s) sur {generalTotals.totalCount || 0} au
                  total
                  {/* 🆕 CORRECTION : Indicateur du filtre de statut actif avec description */}
                  {filters.statusFilter && (
                    <span
                      className={`ml-2 px-2 py-1 text-xs rounded-full ${
                        filters.statusFilter === "depassement"
                          ? "bg-red-100 text-red-700"
                          : filters.statusFilter === "vendu"
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {filters.statusFilter === "depassement"
                        ? "⚠️ Dépassement (non vendus)"
                        : filters.statusFilter === "vendu"
                        ? "💰 Vendus"
                        : "🔄 Recouvrements"}
                    </span>
                  )}
                  {isRestoring && (
                    <span className="ml-2 text-blue-600 text-sm">
                      🔄 Restauration en cours...
                    </span>
                  )}
                </div>

                {/* 🆕 NOUVEAU : Description détaillée du filtre actif */}
                {filters.statusFilter && (
                  <div className="text-sm text-gray-600 italic">
                    {filters.statusFilter === "depassement"
                      ? "Affichage des contrats avec date limite dépassée, non clôturés et non vendus"
                      : filters.statusFilter === "vendu"
                      ? "Affichage des contrats marqués comme vendus dans les remarques"
                      : "Affichage des contrats en cours qui n'ont pas encore été rachetés (clôturés)"}
                  </div>
                )}

                {/* 🆕 NOUVEAU : Indicateur de tri côté serveur */}
                <div className="text-sm text-gray-600">{getSortLabel()}</div>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Groupe des boutons d'action */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                  {/* 🆕 NOUVEAU : Case à cocher Pagination */}
                  <div 
                    className="flex items-center space-x-2 bg-white px-3 py-2 rounded-md border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors h-[38px]"
                    onClick={() => setPaginationEnabled(!paginationEnabled)}
                    title="Décocher pour afficher tous les contrats sur une seule page"
                  >
                    <input
                      type="checkbox"
                      id="paginationEnabled"
                      checked={paginationEnabled}
                      onChange={(e) => {
                        e.stopPropagation();
                        setPaginationEnabled(e.target.checked);
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <label htmlFor="paginationEnabled" className="text-sm text-gray-700 font-medium cursor-pointer select-none">
                      Pagination
                    </label>
                  </div>

                  {/* 🆕 NOUVEAU : Bouton Photothèque */}
                  <button
                    onClick={() => navigate('/phototheque', { state: { filters, paginationEnabled, sorting } })}
                    className={`px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center space-x-2 text-sm font-medium`}
                    title="Ouvrir la photothèque avec les filtres actuels"
                  >
                    <span>📸</span>
                    <span className="hidden sm:inline">Photothèque</span>
                    <span className="sm:hidden">Photo</span>
                  </button>

                  {/* Bouton Statistiques - EXISTANT */}
                  <button
                    onClick={() => setStatisticsModalOpen(true)}
                    disabled={isExporting}
                    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm font-medium ${
                      isExporting ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    title="Voir les statistiques détaillées"
                  >
                    <span>📊</span>
                    <span className="hidden sm:inline">
                      Voir les statistiques
                    </span>
                    <span className="sm:hidden">Stats</span>
                  </button>

                  {/* 🆕 NOUVEAU : Bouton Imprimer PDF */}
                  <button
                    onClick={handleExportPDF}
                    disabled={isExporting || contracts.length === 0}
                    className={`px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2 text-sm font-medium ${
                      isExporting || contracts.length === 0
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    title={
                      contracts.length === 0
                        ? "Aucun contrat à imprimer"
                        : `Imprimer ${generalTotals.totalCount || contracts.length} contrat(s) en PDF`
                    }
                  >
                    {isExporting && exportStatus === "pdf" ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span className="hidden sm:inline">Génération...</span>
                        <span className="sm:hidden">...</span>
                      </>
                    ) : (
                      <>
                        <span>🖨️</span>
                        <span className="hidden sm:inline">Imprimer</span>
                        <span className="sm:hidden">PDF</span>
                      </>
                    )}
                  </button>

                  {/* 🆕 NOUVEAU : Bouton Exporter Excel */}
                  <button
                    onClick={handleExportExcel}
                    disabled={isExporting || contracts.length === 0}
                    className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm font-medium ${
                      isExporting || contracts.length === 0
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    title={
                      contracts.length === 0
                        ? "Aucun contrat à exporter"
                        : `Exporter ${generalTotals.totalCount || contracts.length} contrat(s) en Excel`
                    }
                  >
                    {isExporting && exportStatus === "excel" ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span className="hidden sm:inline">Export...</span>
                        <span className="sm:hidden">...</span>
                      </>
                    ) : (
                      <>
                        <span>📊</span>
                        <span className="hidden sm:inline">Exporter</span>
                        <span className="sm:hidden">Excel</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Section Zoom - EXISTANTE (déplacée à droite) */}
                <div className="flex items-center">
                  <label
                    htmlFor="zoom"
                    className="mr-2 text-sm font-medium text-gray-700"
                  >
                    Zoom:
                  </label>
                  <input
                    type="range"
                    id="zoom"
                    name="zoom"
                    min="50"
                    max="150"
                    step="10"
                    value={tableZoom}
                    onChange={handleZoomChange}
                    className="w-32"
                    disabled={isExporting}
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    {tableZoom}%
                  </span>
                </div>
              </div>

              {/* 🆕 NOUVEAU : Indicateur d'export en cours */}
              {isExporting && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center text-blue-700">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-700 mr-3"></div>
                    <span className="font-medium">
                      {exportStatus === "pdf"
                        ? "🖨️ Génération du PDF en cours..."
                        : "📊 Export Excel en cours..."}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-blue-600">
                    {generalTotals.totalCount || contracts.length} contrat(s) • Filtres :{" "}
                    {Object.keys(filters).filter(
                      (key) => filters[key] && filters[key] !== ""
                    ).length > 0
                      ? Object.entries(filters)
                          .filter(([key, value]) => value && value !== "")
                          .map(([key, value]) => {
                            if (key === "statusFilter") {
                              return value === "depassement"
                                ? "Dépassement"
                                : value === "vendu"
                                ? "Vendus"
                                : "Recouvrements";
                            }
                            return `${key}=${value}`;
                          })
                          .join(", ")
                      : "Aucun"}
                  </div>
                </div>
              )}
            </div>

            <div className={`table-container ${isFullscreen ? 'fullscreen-table' : ''}`} ref={tableContainerRef}>
              <table className="resizable-table">
                <thead>
                  <tr>
                    <th 
                      className="fixed-column fixed-column-1 cursor-pointer hover:bg-gray-200 transition-colors"
                      onDoubleClick={() => setIsFullscreen(!isFullscreen)}
                      title="Double-cliquez pour basculer en plein écran"
                    >
                      N°
                    </th>
                    <th
                      className="fixed-column fixed-column-2 couleur-ligne"
                      onClick={() =>
                        setColorColumnExpanded(!colorColumnExpanded)
                      }
                    >
                      {colorColumnExpanded ? "Couleur" : "C"}
                    </th>
                    <th
                      className="fixed-column fixed-column-3 etat-contrat-col"
                      onClick={() =>
                        setStateColumnExpanded(!stateColumnExpanded)
                      }
                    >
                      {stateColumnExpanded ? "État" : "É"}
                    </th>
                    {/* 🆕 MODIFICATION : En-tête de colonne Contrat avec tri côté serveur */}
                    <th
                      className="fixed-column fixed-column-4 sort-header"
                      onClick={handleSort}
                      title="Cliquer pour trier par numéro de dossier (tous les contrats filtrés)"
                    >
                      <div className="sort-header-content">
                        <span>Contrat</span>
                        {getSortIcon()}
                      </div>
                    </th>
                    <th>
                      Poids
                      <br />
                      bijou
                    </th>
                    <th>CDVA</th>
                    <th>CDD</th>
                    <th>Statut</th>
                    <th>
                      Date
                      <br />
                      création
                    </th>
                    <th>
                      Date
                      <br />
                      limite
                    </th>
                    <th>
                      Montant
                      <br />
                      Décais.
                    </th>
                    <th>
                      Montant
                      <br />
                      Prévu
                    </th>
                    <th>
                      Montant
                      <br />
                      Perçu
                    </th>
                    <th>Reste</th>
                    <th>
                      Dernier
                      <br />
                      Vers.
                    </th>
                    <th>Téléphone</th>
                    <th>Client</th>
                    <th>Date clôture</th>
                    <th>Remise</th>
                    <th>Rmq</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* 🆕 MODIFICATION : Utiliser contracts directement (déjà triés côté serveur) */}

                  {contracts.length === 0 ? (
                    <tr>
                      <td colSpan="22" className="text-center">
                        {filters.statusFilter
                          ? `Aucun contrat ${
                              filters.statusFilter === "depassement"
                                ? "en dépassement d'échéance"
                                : filters.statusFilter === "vendu"
                                ? "vendu"
                                : "en cours (recouvrement)"
                            } trouvé.`
                          : "Aucun contrat trouvé."}
                      </td>
                    </tr>
                  ) : (
                    <>
                      {contracts.map((contract, index) => (
                        <MemoizedTableRow
                          key={contract._id}
                          contract={contract}
                          rowNumber={
                            (pagination.page - 1) * pagination.limit + index + 1
                          }
                          colorColumnExpanded={
                            memoizedProps.colorColumnExpanded
                          }
                          stateColumnExpanded={
                            memoizedProps.stateColumnExpanded
                          }
                          getRowStyle={memoizedProps.getRowStyle}
                          getCellStyle={memoizedProps.getCellStyle}
                          calculateStatus={calculateStatus}
                          getEtatContrat={getEtatContrat}
                          updateEtatContrat={updateEtatContrat}
                          updateBackgroundColor={updateBackgroundColor}
                          formatDecimal={formatDecimal}
                          formatDate={formatDate}
                          formatAmount={formatAmount}
                          isHovered={hoveredRow === contract._id}
                          isSelected={selectedRow === contract._id}
                          onMouseEnter={() => setHoveredRow(contract._id)}
                          onMouseLeave={() => setHoveredRow(null)}
                          onClick={(e) =>
                            memoizedProps.handleRowClick(contract._id, e)
                          }
                          onDoubleClick={(e) =>
                            memoizedProps.handleRowDoubleClick(contract._id, e)
                          }
                          saveNavigationState={
                            memoizedProps.saveNavigationState
                          }
                          navigate={memoizedProps.navigate}
                          contractIds={memoizedProps.contractIds}
                          dragOverRowId={memoizedProps.dragOverRowId}
                          uploadingRowId={memoizedProps.uploadingRowId}
                          handleDragEnterRow={memoizedProps.handleDragEnterRow}
                          handleDragLeaveRow={memoizedProps.handleDragLeaveRow}
                          handleDragOverRow={memoizedProps.handleDragOverRow}
                          handleDropRow={memoizedProps.handleDropRow}
                        />
                      ))}

                      <tr className="totals-row">
                        <td
                          className="fixed-column fixed-column-1"
                          style={{ backgroundColor: "#f1f5f9" }}
                        ></td>
                        <td
                          className="fixed-column fixed-column-2"
                          style={{ backgroundColor: "#f1f5f9" }}
                        >
                          {colorColumnExpanded ? "Total page" : "TP"}
                        </td>
                        <td
                          className="fixed-column fixed-column-3"
                          style={{ backgroundColor: "#f1f5f9" }}
                        ></td>
                        <td
                          className="fixed-column fixed-column-4"
                          style={{ backgroundColor: "#f1f5f9" }}
                        >
                          {totals.contractCount || contracts.length} contrat(s)
                        </td>
                        <td className="text-right">
                          {formatDecimal(totals.jewelWeight)}
                        </td>
                        <td className="text-right">
                          {formatDecimal(totals.cdvaWeight)}
                        </td>
                        <td className="text-right">
                          {formatDecimal(totals.cddWeight)}
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">
                          {formatAmount(totals.disbursementAmount)}
                        </td>
                        <td className="text-right">
                          {formatAmount(totals.expectedAmount)}
                        </td>
                        <td className="text-right">
                          {formatAmount(totals.receivedAmount)}
                        </td>
                        <td className="text-right">
                          {formatAmount(totals.remainingAmount)}
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">
                          {totals.discount > 0
                            ? formatAmount(totals.discount)
                            : ""}
                        </td>
                        <td></td>
                        <td></td>
                      </tr>

                      <tr className="totals-row-global">
                        <td
                          className="fixed-column fixed-column-1"
                          style={{ backgroundColor: "#e2e8f0" }}
                        ></td>
                        <td
                          className="fixed-column fixed-column-2"
                          style={{ backgroundColor: "#e2e8f0" }}
                        >
                          {colorColumnExpanded ? "Total général" : "TG"}
                        </td>
                        <td
                          className="fixed-column fixed-column-3"
                          style={{ backgroundColor: "#e2e8f0" }}
                        ></td>
                        <td
                          className="fixed-column fixed-column-4"
                          style={{ backgroundColor: "#e2e8f0" }}
                        >
                          {generalTotals.totalCount} contrat(s)
                        </td>
                        <td className="text-right">
                          {formatDecimal(generalTotals.jewelWeight)}
                        </td>
                        <td className="text-right">
                          {formatDecimal(generalTotals.cdvaWeight)}
                        </td>
                        <td className="text-right">
                          {formatDecimal(generalTotals.cddWeight)}
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">
                          {formatAmount(generalTotals.disbursementAmount)}
                        </td>
                        <td className="text-right">
                          {formatAmount(generalTotals.expectedAmount)}
                        </td>
                        <td className="text-right">
                          {formatAmount(generalTotals.receivedAmount)}
                        </td>
                        <td className="text-right">
                          {formatAmount(generalTotals.remainingAmount)}
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">
                          {generalTotals.discount > 0
                            ? formatAmount(generalTotals.discount)
                            : ""}
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* 🆕 MODIFICATION : Boutons de pagination minimaux pour le mode plein écran */}
            {isFullscreen && paginationEnabled && pagination.pages > 1 && (
              <div 
                className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full shadow-2xl border border-gray-300 p-2 flex items-center space-x-3 opacity-90 hover:opacity-100 transition-opacity"
                style={{ zIndex: 1001 }}
              >
                <button 
                  onClick={(e) => { e.stopPropagation(); handlePageChange(pagination.page - 1); }}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 font-bold rounded-full ${pagination.page === 1 ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-white bg-blue-600 hover:bg-blue-700'}`}
                  title="Page précédente"
                >
                  &larr; Préc.
                </button>
                <span className="text-sm font-bold text-gray-700 px-2">
                  {pagination.page} / {pagination.pages}
                </span>
                <button 
                  onClick={(e) => { e.stopPropagation(); handlePageChange(pagination.page + 1); }}
                  disabled={pagination.page === pagination.pages}
                  className={`px-3 py-1 font-bold rounded-full ${pagination.page === pagination.pages ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-white bg-blue-600 hover:bg-blue-700'}`}
                  title="Page suivante"
                >
                  Suiv. &rarr;
                </button>
              </div>
            )}

            {paginationEnabled && pagination.pages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ContractStatisticsModal
        isOpen={statisticsModalOpen}
        onClose={() => setStatisticsModalOpen(false)}
        filters={filters}
      />
    </div>
  );
};

export default ContractList;
